
import { GENERIC_ERROR_DESCRIPTION, tenant } from '@/constants';
import { AppRoute } from '@/router/routes';
import { SelectOption } from '@/types';
import { copyToClipboard } from '@/utilities';
import {
  AppleBusinessConnectCompany,
  AppleBusinessConnectLocation,
  AppleBusinessConnectBrand,
} from 'ignite360-core';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { AppleBusinessConnectApi } from '@/api/AppleBusinessConnectApi';

@Component({
  name: 'apple-business-connect-login-card',
})
export default class AppleBusinessConnectLoginCard extends Vue {
  @Prop({ required: true, type: String })
  readonly botId!: string;

  @Prop({ required: true, type: String })
  readonly companyId!: string;

  @Prop({ required: true, type: String })
  readonly locationId!: string;

  @Prop({ required: true, type: String })
  readonly brandId!: string;

  @Prop({ required: false, type: Boolean, default: false })
  readonly hideCopyButton!: boolean;

  @Prop({ required: false, type: String, default: 'Connect to page' })
  readonly header!: string;

  selectedCompanyId = '';
  selectedLocationId = '';
  selectedBrandId = '';
  loadedLocations: AppleBusinessConnectLocation[] = [];
  loadedCompanies: AppleBusinessConnectCompany[] = [];
  loadedBrands: AppleBusinessConnectBrand[] = [];
  isLoading = false;
  isSubmitting = false;
  isDisconnecting = false;
  isPublishingTestPost = false;

  get canPublishTestPost(): boolean {
    return !!this.selectedCompanyId && !!this.selectedLocationId && !!this.selectedBrandId;
  }

  get canSave(): boolean {
    return !!this.selectedCompanyId && !!this.selectedLocationId;
  }

  get canDisconnect(): boolean {
    return !!this.companyId;
  }

  get hasChanges(): boolean {
    return (
      this.selectedCompanyId !== this.companyId ||
      this.selectedLocationId !== this.locationId ||
      this.selectedBrandId !== this.brandId
    );
  }

  get locationOptions(): SelectOption[] {
    return this.loadedLocations.map((location) => ({
      value: location.id,
      label: location.name,
    }));
  }

  get companyOptions(): SelectOption[] {
    return this.loadedCompanies.map((company) => ({
      value: company.id,
      label: company.name,
    }));
  }

  get brandOptions(): SelectOption[] {
    return this.loadedBrands.map((brand) => ({
      value: brand.id,
      label: brand.name,
    }));
  }

  get selectedBrand(): AppleBusinessConnectBrand | undefined {
    return this.loadedBrands.find((brand) => brand.id === this.selectedBrandId);
  }

  get selectedLocation(): AppleBusinessConnectLocation | undefined {
    return this.loadedLocations.find((location) => location.id === this.selectedLocationId);
  }

  async loadLocations(brandId: string) {
    const locations = await AppleBusinessConnectApi.loadLocations(
      this.botId,
      this.selectedCompanyId,
      brandId,
    );

    this.loadedLocations.push(...locations);
  }

  async loadCompany() {
    const company = await AppleBusinessConnectApi.loadCompany(this.botId);

    this.loadedCompanies = [company];
  }

  async loadBrands() {
    const brands = await AppleBusinessConnectApi.loadBrands(this.botId, this.selectedCompanyId);

    this.loadedBrands.push(...brands);
  }

  async onLogin() {
    await this.loadCompany();
  }

  // async mounted() {
  //   await this.loadLocations();
  // }
  async onSubmit() {
    if (!this.selectedLocation) return;
    this.isSubmitting = true;
    try {
      await AppleBusinessConnectApi.connectBotWithAppleBusinessConnectLocation(this.botId, {
        locationId: this.selectedLocation.id,
        companyId: this.selectedCompanyId,
        locationName: this.selectedLocation.name,
        brandId: this.selectedBrand?.id || '',
        brandName: this.selectedBrand?.name || '',
      });
      this.$emit('connect', {
        locationId: this.selectedLocation.id,
        locationName: this.selectedLocation.name,
        companyId: this.selectedCompanyId,
        brandId: this.selectedBrand?.id || '',
        brandName: this.selectedBrand?.name || '',
      });
      this.$notify.success('Successfully connected to Apple Business Connect');
    } catch (e) {
      this.$notify.error({
        title: 'Connecting to Apple Business Connect failed',
        description: GENERIC_ERROR_DESCRIPTION,
      });
    } finally {
      this.isSubmitting = false;
    }
  }

  async onDisconnect() {
    if (!this.canDisconnect) return;
    this.isDisconnecting = true;
    try {
      await AppleBusinessConnectApi.disconnectBotFromAppleBusinessConnectLocation(this.botId);
      this.$emit('disconnect');
      this.$notify.success('Successfully disconnected from Apple Business Connect');
    } catch (e) {
      this.$notify.error({
        title: 'Disconnecting from Apple Business Connect failed',
        description: GENERIC_ERROR_DESCRIPTION,
      });
    } finally {
      this.isDisconnecting = false;
    }
  }

  copyPublicLoginUrl() {
    const resolvedRoute = this.$router.resolve({
      name: AppRoute.PublicSocialLoginAppleBusinessConnect,
      params: { id: this.botId },
    });
    copyToClipboard(location.origin + resolvedRoute.href);
  }

  @Watch('companyId', { immediate: true })
  watchSelectedCompany() {
    this.selectedCompanyId = this.companyId;
  }
  @Watch('locationId', { immediate: true })
  watchSelectedLocation() {
    this.selectedLocationId = this.locationId;
  }
  @Watch('brandId', { immediate: true })
  watchSelectedBrand() {
    this.selectedBrandId = this.brandId;
  }

  @Watch('selectedCompanyId', { immediate: true })
  async watchSelectedCompanyId() {
    if (this.selectedCompanyId) {
      await this.loadBrands();
    }
  }

  @Watch('selectedBrandId', { immediate: true })
  async watchSelectedBrandId() {
    if (this.selectedBrandId) {
      await this.loadLocations(this.selectedBrandId);
    }
  }

  @Watch('botId', { immediate: true })
  async onBotChange() {
    this.loadedLocations = [];
    this.loadedCompanies = [];
    this.isLoading = true;
    try {
      await Promise.all([this.loadCompany()]);
    } catch (e) {
      // ignore the errors for LinkedIn
    } finally {
      this.isLoading = false;
    }
  }

  async publishTestPost() {
    this.isPublishingTestPost = true;
    try {
      await AppleBusinessConnectApi.publishTestPost(this.botId);
    } catch (e) {
      this.$notify.error({
        title: 'Publishing a test post failed',
        description: GENERIC_ERROR_DESCRIPTION,
      });
    } finally {
      this.isPublishingTestPost = false;
    }
  }
}
